/**
 * Teslyn Support access (decision 800; docs/specs/cloud/support-access.md; 20261030130000_support_access.sql). A
 * tenant admin grants the Teslyn Support team access for a set time, ends it early and reads the record; a platform
 * admin opens a grant in force and Teslyn Cloud records each page they view. The database decides every step.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { isSupportScope, type SupportGrant, type SupportScope } from '@teslyn/domain';

const GRANT_COLUMNS =
  'id, tenant_id, scope, starts_at, ends_at, reason, granted_by, granted_at, revoked_at, revoked_by, first_used_at';

/** "relation does not exist" / "function not found": the database update of 30 Oct 2026 is not applied yet. */
export const notMigrated = (e: { readonly code?: string; readonly message?: string } | null): boolean =>
  Boolean(
    e &&
    (e.code === '42P01' ||
      e.code === 'PGRST202' ||
      e.code === 'PGRST205' ||
      /does not exist|could not find/i.test(e.message ?? '')),
  );

/** The database's own words (its refusals are written for people: two-step sign-in, tenant admins only, …). */
function fail(e: { readonly message: string } | null): void {
  if (e) throw new Error(e.message);
}

function grantOf(r: Record<string, unknown>): SupportGrant {
  return {
    id: String(r.id),
    tenant_id: String(r.tenant_id),
    scope: isSupportScope(r.scope) ? r.scope : 'read',
    starts_at: String(r.starts_at),
    ends_at: String(r.ends_at),
    reason: String(r.reason ?? ''),
    granted_by: (r.granted_by as string | null) ?? null,
    granted_at: String(r.granted_at),
    revoked_at: (r.revoked_at as string | null) ?? null,
    revoked_by: (r.revoked_by as string | null) ?? null,
    first_used_at: (r.first_used_at as string | null) ?? null,
  };
}

/** One line of the support record: an audit row of kind support.*. */
export interface SupportEvent {
  readonly id: number;
  readonly at: string;
  readonly actorId: string | null;
  readonly action: string;
  readonly target: string;
  readonly detail: Record<string, unknown>;
}

export interface SupportOverview {
  /** null while the database update is not applied. */
  readonly grants: readonly SupportGrant[] | null;
  readonly events: readonly SupportEvent[];
}

/** The company's grants (newest first) and its support record (the last 200 rows). */
export async function loadSupportOverview(sb: SupabaseClient, tenantId: string): Promise<SupportOverview> {
  const [g, a] = await Promise.all([
    sb
      .from('support_access')
      .select(GRANT_COLUMNS)
      .eq('tenant_id', tenantId)
      .order('granted_at', { ascending: false })
      .limit(50),
    sb
      .from('audit_event')
      .select('id, at, actor_id, action, target, detail')
      .eq('tenant_id', tenantId)
      .like('action', 'support.%')
      .order('id', { ascending: false })
      .limit(200),
  ]);
  if (notMigrated(g.error)) return { grants: null, events: [] };
  fail(g.error);
  fail(a.error);
  return {
    grants: ((g.data ?? []) as Record<string, unknown>[]).map(grantOf),
    events: ((a.data ?? []) as Record<string, unknown>[]).map((r) => ({
      id: Number(r.id),
      at: String(r.at),
      actorId: (r.actor_id as string | null) ?? null,
      action: String(r.action),
      target: String(r.target ?? ''),
      detail: (r.detail as Record<string, unknown> | null) ?? {},
    })),
  };
}

export async function grantSupport(
  sb: SupabaseClient,
  tenantId: string,
  scope: SupportScope,
  hours: number,
  reason: string,
): Promise<SupportGrant> {
  const { data, error } = await sb.rpc('support_access_grant', {
    p_tenant: tenantId,
    p_scope: scope,
    p_hours: hours,
    p_reason: reason.trim(),
  });
  fail(error);
  return grantOf(data as Record<string, unknown>);
}

export async function revokeSupport(sb: SupabaseClient, grantId: string): Promise<void> {
  const { error } = await sb.rpc('support_access_revoke', { p_grant: grantId });
  fail(error);
}

/** A platform admin's view of one company's support access: the grant in force, if any. */
export interface MySupport {
  readonly tenantId: string;
  readonly grantId: string | null;
  readonly scope: SupportScope;
  readonly endsAt: string | null;
  /** The staging-only standing access is on (decision 800): no grant needed. */
  readonly standing: boolean;
}

/**
 * For a platform admin on a company's portal: the grant in force there now, or the staging switch; null when
 * neither (or before the database update).
 */
export async function loadMySupport(sb: SupabaseClient, tenantId: string): Promise<MySupport | null> {
  const [g, s] = await Promise.all([
    sb
      .from('support_access')
      .select(GRANT_COLUMNS)
      .eq('tenant_id', tenantId)
      .is('revoked_at', null)
      .lte('starts_at', new Date().toISOString())
      .gt('ends_at', new Date().toISOString())
      .order('ends_at', { ascending: false }),
    sb.from('platform_setting').select('key, value').eq('key', 'support_standing_access').maybeSingle(),
  ]);
  if (g.error) return null;
  const standing = !s.error && (s.data as { value?: string | null } | null)?.value === 'on';
  const rows = ((g.data ?? []) as Record<string, unknown>[]).map(grantOf);
  const best = rows.find((r) => r.scope === 'change') ?? rows[0] ?? null;
  if (!best && !standing) return null;
  return {
    tenantId,
    grantId: best?.id ?? null,
    scope: best ? best.scope : 'change',
    endsAt: best?.ends_at ?? null,
    standing,
  };
}

/** Start using the grant in force (recorded once per person and grant). Needs two-step sign-in in this session. */
export async function openSupport(sb: SupabaseClient, tenantId: string): Promise<void> {
  const { error } = await sb.rpc('support_access_open', { p_tenant: tenantId });
  fail(error);
}

/** Whether support access still stands, as the database says now. */
export type SupportCheck =
  | { readonly state: 'in_force'; readonly endsAt: string | null }
  | { readonly state: 'ended' }
  /** No answer (a dropped connection): the known end still applies. */
  | { readonly state: 'unknown' };

/**
 * Ask whether support access stands, by opening it again: already opened is not recorded again; a newer grant in force
 * is opened and recorded, as entering would. A refusal (42501) means it ended: revoked, run out, or the session lost
 * its two-step sign-in.
 */
export async function checkSupport(sb: SupabaseClient, tenantId: string): Promise<SupportCheck> {
  const { data, error } = await sb.rpc('support_access_open', { p_tenant: tenantId });
  if (error) return error.code === '42501' ? { state: 'ended' } : { state: 'unknown' };
  return { state: 'in_force', endsAt: (data as { ends_at?: string } | null)?.ends_at ?? null };
}

/** Record a page opened with support access (best effort: the database ignores repeats within 10 minutes). */
export async function noteSupportView(sb: SupabaseClient, tenantId: string, page: string): Promise<void> {
  await sb.rpc('support_access_viewed', { p_tenant: tenantId, p_page: page });
}
